import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, InfoTable, MarkdownBase, PageWrapper, SectionHeading } from '@/components';
import { usePolling } from '@/hooks';
import { NmibleService } from '@/services';
import { useConfig, useUI } from '@/store';
import { APP_CONFIG, NmibleService as NS, ROUTE } from '@/types';
import { ResolveDate } from '@/utils';
const resolveDate = new ResolveDate();
const virtualCardAspectRatio = '288 / 181';
const PaymentVirtualCardView = ({ balanceLabel, balance }) => {
    const [virtualCardHTML, setVirtualCardHTML] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        let isActive = true;
        NmibleService.showVirtualCard()
            .then((html) => {
            if (isActive)
                setVirtualCardHTML(html);
        })
            .catch(() => {
            if (isActive)
                setVirtualCardHTML(null);
        })
            .finally(() => {
            if (isActive)
                setIsLoading(false);
        });
        return () => {
            isActive = false;
        };
    }, []);
    if (isLoading) {
        return (_jsxs(VStack, Object.assign({ align: 'start', spacing: '3' }, { children: [_jsx(Skeleton, { borderRadius: 'md', w: '392px', maxW: '392px', minW: '320px', sx: { aspectRatio: virtualCardAspectRatio } }), balance && _jsx(InfoTable.Skeleton, {})] })));
    }
    if (!virtualCardHTML)
        return null;
    return (_jsxs(VStack, Object.assign({ align: 'start', spacing: '3' }, { children: [_jsx(Box, { as: 'iframe', title: 'Virtual card', srcDoc: virtualCardHTML, sandbox: 'allow-same-origin allow-scripts', border: 'none', borderRadius: 'md', w: '100%', maxW: '392px', minW: '320px', sx: { aspectRatio: virtualCardAspectRatio } }), balance && (_jsx(InfoTable, { children: _jsx(InfoTable.Row, { label: balanceLabel, value: `${balance.balance} ${balance.currency}` }) }))] })));
};
const PaymentTransactionRow = ({ transaction }) => (_jsxs(HStack, Object.assign({ align: 'center', justify: 'space-between', color: 'darkBrandBlue.90', px: '3', py: '2', borderRadius: 'md', transition: 'background 0.2s', _hover: { bg: 'darkBrandBlue.5' } }, { children: [_jsxs(HStack, Object.assign({ spacing: '2', align: 'center' }, { children: [_jsx(Icon, { name: 'calendar', size: '20px', p: '0' }), _jsx(Text, { children: resolveDate.formatDateLocalized(new Date(transaction.created_at)).date_time })] })), _jsx(Text, { children: transaction.amount })] })));
export const Payment = () => {
    var _a;
    const { t } = useTranslation();
    const { getComponentConfig } = useConfig();
    const { withLoading } = useUI();
    const navigate = useNavigate();
    const isFirstStatusRequestRef = useRef(true);
    const config = getComponentConfig(APP_CONFIG.COMPONENTS.ConfigComponentType.PAYMENT);
    const pollingCallback = () => {
        const getStatus = () => NmibleService.getStatus({
            show_payment_details: true,
            show_transactions: true
        });
        if (!isFirstStatusRequestRef.current)
            return getStatus();
        isFirstStatusRequestRef.current = false;
        return withLoading(getStatus);
    };
    const { data: status } = usePolling(pollingCallback, {
        deps: [withLoading],
        interval: 8000
    });
    const paymentDetails = status === null || status === void 0 ? void 0 : status.payment_details;
    const hasPaymentSetup = Boolean((status === null || status === void 0 ? void 0 : status.payment_setup_complete) && paymentDetails);
    const isVirtualCard = (paymentDetails === null || paymentDetails === void 0 ? void 0 : paymentDetails.type) === NS.PaymentDetailsType.VIRTUAL_CARD;
    const transactions = (_a = status === null || status === void 0 ? void 0 : status.transactions) !== null && _a !== void 0 ? _a : [];
    const PaymentDetailsView = hasPaymentSetup && paymentDetails ? (_jsx(VStack, Object.assign({ align: 'stretch', spacing: '4' }, { children: isVirtualCard ? (_jsx(PaymentVirtualCardView, { balanceLabel: t(config === null || config === void 0 ? void 0 : config.balance_text), balance: status === null || status === void 0 ? void 0 : status.virtual_card_available_balance })) : (_jsx(InfoTable, { children: _jsx(InfoTable.Row, { label: paymentDetails.title, value: paymentDetails.identifier }) })) }))) : null;
    const TransactionsView = transactions.length > 0 ? (_jsxs(VStack, Object.assign({ align: 'stretch', mt: '12' }, { children: [_jsx(SectionHeading, { title: t(config === null || config === void 0 ? void 0 : config.title_history) }), transactions.map((transaction) => (_jsx(PaymentTransactionRow, { transaction: transaction }, transaction.created_at)))] }))) : null;
    return (_jsxs(PageWrapper, Object.assign({ title: t(config === null || config === void 0 ? void 0 : config.title) }, { children: [_jsxs(VStack, Object.assign({ align: 'start', spacing: '6' }, { children: [_jsx(MarkdownBase, { color: 'darkBrandBlue.90', markdown: t(config === null || config === void 0 ? void 0 : config.content) }), PaymentDetailsView, _jsx(Button, Object.assign({ w: 'fit-content', onClick: () => navigate(ROUTE.PAYMENT_SETUP) }, { children: hasPaymentSetup
                            ? t(config === null || config === void 0 ? void 0 : config.update_payment_button_text)
                            : t(config === null || config === void 0 ? void 0 : config.setup_payment_button_text) }))] })), TransactionsView] })));
};
